import { useState, useMemo } from 'react'
import { toast } from 'sonner'
import type { ColumnDef } from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DataTable } from '@/shared/components/ui/data-table'
import { Switch } from '@/components/ui/switch'
import { Copy, Trash2, Search, Hash, CheckCircle, Calendar, Power } from 'lucide-react'
import type { Codigo } from '../types'

interface CodigosTableProps {
  codigos: Codigo[]
  onToggle: (codigoId: number) => void
  onDelete: (codigoId: number) => void
  actions?: React.ReactNode
}

const copyToClipboard = (text: string) => {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.cssText = 'position:fixed;left:0;top:0;opacity:0;'
  document.body.appendChild(textarea)
  textarea.focus()
  textarea.select()
  let copied = false
  try {
    copied = document.execCommand('copy')
  } catch {
    copied = false
  }
  document.body.removeChild(textarea)
  if (copied) {
    toast.success('Código copiado al portapapeles')
  } else {
    toast.info(`Código: ${text}`, { duration: 10000 })
  }
}

export function CodigosTable({ codigos, onToggle, onDelete, actions }: CodigosTableProps) {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('todos')

  const filtered = useMemo(() => {
    let result = codigos
    if (search) {
      result = result.filter(c => c.codigo.toLowerCase().includes(search.toLowerCase()))
    }
    if (statusFilter === 'disponible') {
      result = result.filter(c => !c.canjeado)
    } else if (statusFilter === 'canjeado') {
      result = result.filter(c => c.canjeado)
    }
    return result
  }, [codigos, search, statusFilter])

  const columns: ColumnDef<Codigo>[] = [
    {
      accessorKey: 'codigo',
      header: () => (
        <div className="flex items-center gap-2">
          <Hash className="h-4 w-4" />
          Código
        </div>
      ),
      cell: ({ row }) => (
        <span className="font-mono font-bold">{row.getValue('codigo')}</span>
      ),
    },
    {
      accessorKey: 'canjeado',
      header: () => (
        <div className="flex items-center gap-2">
          <CheckCircle className="h-4 w-4" />
          Estado
        </div>
      ),
      cell: ({ row }) => {
        const canjeado = row.getValue('canjeado') as boolean
        return (
          <Badge className={canjeado ? 'badge-gradient hover:opacity-90' : ''} variant={canjeado ? 'default' : 'secondary'}>
            {canjeado ? 'Canjeado' : 'Disponible'}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'fecha_creacion',
      header: () => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Creación
        </div>
      ),
      cell: ({ row }) => {
        const fecha = row.getValue('fecha_creacion') as string | null
        return <span className="text-sm">{fecha ? new Date(fecha).toLocaleDateString('es-EC') : '-'}</span>
      },
    },
    {
      accessorKey: 'fecha_canje',
      header: () => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Canje
        </div>
      ),
      cell: ({ row }) => {
        const fecha = row.getValue('fecha_canje') as string | null
        return <span className="text-sm">{fecha ? new Date(fecha).toLocaleDateString('es-EC') : '-'}</span>
      },
    },
    {
      accessorKey: 'activo',
      header: () => (
        <div className="flex items-center gap-2">
          <Power className="h-4 w-4" />
          Activo
        </div>
      ),
      cell: ({ row }) => {
        const activo = row.getValue('activo') as boolean
        return (
          <Badge variant={activo ? 'default' : 'destructive'}>
            {activo ? 'Sí' : 'No'}
          </Badge>
        )
      },
    },
    {
      id: 'acciones',
      header: () => <div className="text-right"></div>,
      cell: ({ row }) => {
        const codigo = row.original
        return (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => copyToClipboard(codigo.codigo)}
              title="Copiar código"
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Switch
              checked={codigo.activo}
              onCheckedChange={() => onToggle(codigo.id)}
              className={codigo.activo ? 'data-[state=checked]:bg-teal-500' : ''}
            />
            {!codigo.canjeado && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                onClick={() => onDelete(codigo.id)}
                title="Eliminar"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar código..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="disponible">Disponibles</SelectItem>
            <SelectItem value="canjeado">Canjeados</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">
          {filtered.length} código{filtered.length !== 1 ? 's' : ''}
        </span>
        {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
      </div>

      <DataTable columns={columns} data={filtered} defaultPageSize={10} />
    </div>
  )
}
